import { useEffect, useRef } from 'react';
// Elements that can receive keyboard focus. `:not([disabled])` keeps
// disabled buttons (e.g. a not-yet-enabled Submit) out of the cycle;
// they re-enter automatically because the list is recomputed on each
// Tab keypress.
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
export function useFocusTrap(ref, { onEscape, active = true } = {}) {
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;
    useEffect(() => {
        if (!active)
            return;
        const container = ref.current;
        if (!container)
            return;
        const previouslyFocused = document.activeElement;
        function focusables() {
            if (!container)
                return [];
            return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
        }
        // Move focus into the panel on activate.
        const initial = focusables();
        const firstInitial = initial[0];
        if (firstInitial) {
            firstInitial.focus();
        }
        else {
            container.focus();
        }
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                onEscapeRef.current?.();
                return;
            }
            if (e.key !== 'Tab')
                return;
            const items = focusables();
            const first = items[0];
            const last = items[items.length - 1];
            if (!first || !last) {
                // Nothing to focus — keep focus pinned inside the dialog.
                e.preventDefault();
                return;
            }
            const activeEl = document.activeElement;
            if (e.shiftKey) {
                if (activeEl === first || container === null || !container.contains(activeEl)) {
                    e.preventDefault();
                    last.focus();
                }
            }
            else if (activeEl === last || container === null || !container.contains(activeEl)) {
                e.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, [ref, active]);
}
//# sourceMappingURL=useFocusTrap.js.map